/**
 * Tiled 맵 편집(순수) — 관리자 존 배치 편집기용. docs/build/09 §6-B.
 * 입력·출력 모두 동일한 Tiled JSON(앱 로더 무수정). 48px 그리드 스냅(불변식 4).
 * 객체 식별 키 = name(생성 맵에서 name=id). 절대 throw 안 함.
 */
// collisionFine 은 tiledEdit 의 **타입만** 가져간다(type-only) — 런타임 순환 없음.
import { collisionDiv, isBlockedSub } from "./collisionFine";

export interface TiledProperty {
  name: string;
  type?: string;
  value: string | number | boolean;
}
export interface TiledObject {
  id?: number | string;
  name?: string; // 원본 Tiled JSON엔 없을 수 있음(편집기 생성 객체는 항상 채움)
  type?: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  properties?: TiledProperty[];
}
export interface TiledLayer {
  type: "tilelayer" | "objectgroup" | string;
  name: string;
  data?: number[];
  objects?: TiledObject[];
  width?: number;
  height?: number;
  visible?: boolean;
  opacity?: number;
}
export interface TiledMap {
  width: number;
  height: number;
  tilewidth: number;
  tileheight: number;
  layers: TiledLayer[];
  [k: string]: unknown;
}

export function snap(value: number, grid: number): number {
  if (!grid) return value;
  return Math.round(value / grid) * grid;
}

export function objectLayer(map: TiledMap): TiledLayer | undefined {
  return map.layers?.find((l) => l.type === "objectgroup");
}
export function listObjects(map: TiledMap): TiledObject[] {
  return objectLayer(map)?.objects ?? [];
}

function mapObjects(
  map: TiledMap,
  fn: (objs: TiledObject[]) => TiledObject[],
): TiledMap {
  return {
    ...map,
    layers: map.layers.map((l) =>
      l.type === "objectgroup" ? { ...l, objects: fn(l.objects ?? []) } : l,
    ),
  };
}

/** 객체를 그리드 스냅 + 맵 경계 안으로 이동(name 기준). */
export function moveObject(
  map: TiledMap,
  name: string,
  x: number,
  y: number,
): TiledMap {
  const tile = map.tilewidth || 48;
  const maxX = (map.width - 1) * tile;
  const maxY = (map.height - 1) * tile;
  const nx = Math.max(0, Math.min(snap(x, tile), maxX));
  const ny = Math.max(0, Math.min(snap(y, tile), maxY));
  return mapObjects(map, (objs) =>
    objs.map((o) => (o.name === name ? { ...o, x: nx, y: ny } : o)),
  );
}

export function addObject(map: TiledMap, obj: TiledObject): TiledMap {
  const tile = map.tilewidth || 48;
  const placed: TiledObject = {
    ...obj,
    x: snap(obj.x, tile),
    y: snap(obj.y, tile),
    width: obj.width ?? tile,
    height: obj.height ?? tile,
  };
  return mapObjects(map, (objs) => [...objs, placed]);
}

/**
 * 🗺️ **저장소 지도에만 있는 물건**(이름 기준) — 게시본(아이가 보는 지도)에는 없는 것.
 * 관리자 [🗺️ 체험공간 배치]의 「저장소에만 있는 물건」 카드가 이걸로 고른다(2026-10-03).
 *
 * 🔴 왜: 9. 29. 무대 옆문(portal_event_side)을 저장소 지도에 넣었는데, 게시본이 저장소를 **통째로 이겨서**
 *    아이 화면엔 안 나갔다. 운영자가 올리려면 물건 추가·칸 맞추기·속성 둘 적기를 손으로 해야 했다(일곱 단계).
 *    판정은 `scripts/check-maps-fresh.mjs` 의 「저장소에만 있는 물건」과 같다(이름이 게시본에 없으면).
 * 이름 없는 물건은 고를 수 없어 뺀다.
 */
export function objectsMissingFromLive(live: TiledMap, repo: TiledMap): TiledObject[] {
  const all = (m: TiledMap) => (m.layers ?? []).flatMap((l) => (l.type === "objectgroup" ? (l.objects ?? []) : []));
  const have = new Set(all(live).map((o) => String(o.name ?? "")));
  return all(repo).filter((o) => !!o.name && !have.has(String(o.name)));
}

/**
 * 저장소 물건을 **편집 중인 지도에 더한다** — 자리·속성·번호(id)는 저장소 그대로.
 * 우리 지도는 번호가 이름과 같은 글자다(`portal_event_side`). 번호가 이 지도의 다른 물건과 겹칠 때만 새로 붙인다
 * (글자 번호면 이름으로, 숫자 번호면 가장 큰 번호 + 1).
 * 이미 같은 이름이 있으면 아무것도 안 한다(덮어쓰지 않는다 — 게시본에서 운영자가 고친 것을 지우면 안 된다).
 */
export function adoptObject(map: TiledMap, obj: TiledObject): TiledMap {
  if (!obj.name || listObjects(map).some((o) => o.name === obj.name)) return map;
  const all = (map.layers ?? []).flatMap((l) => l.objects ?? []);
  const taken = new Set(all.map((o) => String(o.id)));
  let id = obj.id;
  if (id === undefined || taken.has(String(id))) {
    if (typeof obj.id === "number") {
      const nums = all.map((o) => Number(o.id)).filter(Number.isFinite);
      id = Math.max(0, ...nums) + 1;
    } else id = obj.name;
  }
  const out = addObject(map, { ...obj, id });
  return typeof id === "number" && "nextobjectid" in map ? { ...out, nextobjectid: id + 1 } : out;
}

export function removeObject(map: TiledMap, name: string): TiledMap {
  return mapObjects(map, (objs) => objs.filter((o) => o.name !== name));
}

export function getProp(
  obj: TiledObject,
  name: string,
): string | number | boolean | undefined {
  return obj.properties?.find((p) => p.name === name)?.value;
}

/** 객체 속성 설정(없으면 추가, 빈 문자열이면 제거). 타입은 자동 추론. */
export function setProp(
  map: TiledMap,
  objName: string,
  propName: string,
  value: string | number | boolean | "",
): TiledMap {
  return mapObjects(map, (objs) =>
    objs.map((o) => {
      if (o.name !== objName) return o;
      let props = (o.properties ?? []).filter((p) => p.name !== propName);
      if (value !== "") {
        const type =
          typeof value === "number" ? "int" : typeof value === "boolean" ? "bool" : "string";
        props = [...props, { name: propName, type, value }];
      }
      return { ...o, properties: props };
    }),
  );
}

// ── z(깊이 바이어스) / 앞뒤 순서 ─────────────────────────────
// 월드는 Y정렬(중심 y가 클수록 앞)로 렌더한다. z 는 그 위에 더해지는 보정값 —
// 겹치는 오브젝트의 앞/뒤를 운영자가 직접 고르게 한다. 없으면 0(순수 Y정렬).
export function getObjectZ(obj: TiledObject): number {
  const raw = getProp(obj, "z");
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : 0;
}
function objectCenterY(obj: TiledObject, tile: number): number {
  return obj.y + (obj.height ?? tile) / 2; // mapLoader py 와 동일 규칙
}
/** 렌더 깊이(중심 y + z). 값이 클수록 앞(위에). */
export function objectEffectiveDepth(obj: TiledObject, tile: number): number {
  return objectCenterY(obj, tile) + getObjectZ(obj);
}
export type ZOrderOp = "front" | "back" | "forward" | "backward";
const Z_STEP = 16; // 순서 변경 시 이웃과 벌리는 간격
const Z_CLAMP = 4000; // 반딧불(9000)·라벨(20000) 아래로 유지

/** 앞/뒤 순서 코어(순수) — 현재 깊이·이웃 깊이들·조작 → 새 깊이.
 *  월드(Y정렬+z 바이어스)와 랜딩(z=CSS zIndex, 깊이 그 자체) 공용. 이미 맨앞/맨뒤면 유지. */
export function reorderDepth(currentEff: number, othersEff: number[], op: ZOrderOp, step: number): number {
  if (op === "front") return (othersEff.length ? Math.max(...othersEff) : currentEff) + step;
  if (op === "back") return (othersEff.length ? Math.min(...othersEff) : currentEff) - step;
  if (op === "forward") {
    const above = othersEff.filter((e) => e > currentEff).sort((a, b) => a - b)[0]; // 바로 앞
    return above !== undefined ? above + step : currentEff;
  }
  const below = othersEff.filter((e) => e < currentEff).sort((a, b) => b - a)[0]; // 바로 뒤
  return below !== undefined ? below - step : currentEff;
}

/** 앞/뒤 순서 변경 → z 속성 갱신. 맨앞/맨뒤는 다른 오브젝트 전체 기준, 앞으로/뒤로는 인접 오브젝트 교차. */
export function reorderObject(map: TiledMap, objName: string, op: ZOrderOp, tile: number): TiledMap {
  const objs = listObjects(map);
  const target = objs.find((o) => o.name === objName);
  if (!target) return map;
  const centerY = objectCenterY(target, tile);
  const targetEff = centerY + getObjectZ(target);
  const othersEff = objs.filter((o) => o !== target).map((o) => objectEffectiveDepth(o, tile));
  const newEff = reorderDepth(targetEff, othersEff, op, Z_STEP);
  const newZ = Math.max(-Z_CLAMP, Math.min(Z_CLAMP, Math.round(newEff - centerY)));
  return setProp(map, objName, "z", newZ === 0 ? "" : newZ);
}

/** 객체 type 변경(인터랙션 종류). */
export function setObjectType(map: TiledMap, objName: string, type: string): TiledMap {
  return mapObjects(map, (objs) =>
    objs.map((o) => (o.name === objName ? { ...o, type } : o)),
  );
}

/** 충돌 타일 토글(그리드 좌표). collision tilelayer data 갱신. */
export function toggleCollision(map: TiledMap, gx: number, gy: number): TiledMap {
  const idx = gy * map.width + gx;
  if (gx < 0 || gy < 0 || gx >= map.width || gy >= map.height) return map;
  return {
    ...map,
    layers: map.layers.map((l) => {
      if (l.type === "tilelayer" && l.name === "collision" && Array.isArray(l.data)) {
        const data = [...l.data];
        data[idx] = (data[idx] ?? 0) !== 0 ? 0 : 1;
        return { ...l, data };
      }
      return l;
    }),
  };
}

export function isBlockedTile(map: TiledMap, gx: number, gy: number): boolean {
  const layer = map.layers.find((l) => l.type === "tilelayer" && l.name === "collision");
  if (!layer?.data) return false;
  return (layer.data[gy * map.width + gx] ?? 0) !== 0;
}

// ── 바닥 타일 팔레트(개별 칠하기) ─────────────────────────────
export interface FloorTile {
  gid: number; // 맵 floor 레이어 data 값(0=없음/기본)
  id: string;
  label: string;
  key: string; // 매니페스트 텍스처 키
  color: string; // 편집기 표시색
}
export const FLOOR_PALETTE: FloorTile[] = [
  { gid: 1, id: "grass", label: "잔디", key: "floor.grass", color: "#8fbf63" },
  { gid: 2, id: "path", label: "흙길", key: "floor.path", color: "#c9a86a" },
  { gid: 3, id: "wood", label: "마루", key: "floor.wood", color: "#cdab7c" },
  { gid: 4, id: "stone", label: "돌바닥", key: "floor.stone", color: "#b6ada0" },
  { gid: 5, id: "sand", label: "모래", key: "floor.sand", color: "#e6d4a0" },
  { gid: 6, id: "water", label: "물", key: "floor.water", color: "#79c3d6" },
];
export const floorByGid = (gid: number): FloorTile | undefined =>
  FLOOR_PALETTE.find((f) => f.gid === gid);

export function floorLayer(map: TiledMap): TiledLayer | undefined {
  return map.layers.find((l) => l.type === "tilelayer" && l.name === "floor");
}
function ensureFloorLayer(map: TiledMap): TiledMap {
  if (floorLayer(map)) return map;
  const layer: TiledLayer = {
    type: "tilelayer",
    name: "floor",
    width: map.width,
    height: map.height,
    data: new Array(map.width * map.height).fill(0),
    visible: true,
    opacity: 1,
  };
  return { ...map, layers: [layer, ...map.layers] }; // 바닥은 맨 아래(먼저)
}
export function getFloorCell(map: TiledMap, gx: number, gy: number): number {
  const l = floorLayer(map);
  if (!l?.data || gx < 0 || gy < 0 || gx >= map.width || gy >= map.height) return 0;
  return l.data[gy * map.width + gx] ?? 0;
}
/** 바닥 셀 설정(gid=0 지우개). floor 레이어 없으면 생성. */
export function setFloorCell(map: TiledMap, gx: number, gy: number, gid: number): TiledMap {
  if (gx < 0 || gy < 0 || gx >= map.width || gy >= map.height) return map;
  const m = ensureFloorLayer(map);
  const idx = gy * m.width + gx;
  return {
    ...m,
    layers: m.layers.map((l) => {
      if (l.type === "tilelayer" && l.name === "floor" && Array.isArray(l.data)) {
        const data = [...l.data];
        data[idx] = gid;
        return { ...l, data };
      }
      return l;
    }),
  };
}

/** 맵 크기 변경(타일 단위). 모든 tilelayer data 를 새 격자로 재배치(겹치는 셀 보존),
 *  오브젝트는 새 경계 안으로 클램프. width/height 갱신. 4~64 클램프. */
export function resizeMap(map: TiledMap, cols: number, rows: number): TiledMap {
  const w = Math.max(4, Math.min(64, Math.floor(cols) || map.width));
  const h = Math.max(4, Math.min(64, Math.floor(rows) || map.height));
  if (w === map.width && h === map.height) return map;
  const tile = map.tilewidth || 48;
  const remap = (data: number[]): number[] => {
    const out = new Array(w * h).fill(0);
    const cw = Math.min(w, map.width);
    const ch = Math.min(h, map.height);
    for (let y = 0; y < ch; y++) for (let x = 0; x < cw; x++) out[y * w + x] = data[y * map.width + x] ?? 0;
    return out;
  };
  const layers = map.layers.map((l) => {
    if (l.type === "tilelayer" && Array.isArray(l.data)) return { ...l, width: w, height: h, data: remap(l.data) };
    if (l.type === "objectgroup") {
      const objs = (l.objects ?? []).map((o) => ({
        ...o,
        x: Math.max(0, Math.min(o.x, (w - 1) * tile)),
        y: Math.max(0, Math.min(o.y, (h - 1) * tile)),
      }));
      return { ...l, objects: objs };
    }
    return l;
  });
  return { ...map, width: w, height: h, layers };
}

// ── 맵 저장 전 검증(도달성) — FR-ADMIN-15 보강 ─────────────────
// 실사고 사례: 충돌 편집 후 포털이 지형에 포위돼 입장 불가(관리자가 눈으로 못 잡음).
// 저장/게시 직전에 호출해 경고한다. 순수 함수(관리자 UI·스크립트 공용).

export interface MapIssue {
  kind: "portal_unreachable" | "npc_unreachable" | "isolated_pockets" | "no_walkable";
  name?: string; // 대상 오브젝트 이름
  detail: string; // 사람이 읽는 설명(한국어)
}

/**
 * 스폰에서 4방향 BFS — 도달 가능한 **타일** 집합.
 *
 * 탐색은 **조각 단위**로 한다. 타일 단위로 보면 모서리만 살짝 막은 타일이 통째로 막힌 것이
 * 되어, 운영자가 잘게 칠할수록 "갇힌 곳"이 늘었다고 잘못 경고한다. 조각으로 지나갈 수 있으면
 * 지나갈 수 있는 것이다. 결과는 타일 키로 돌려준다 — 포털·NPC 검사가 타일 좌표로 돌아간다.
 */
function reachableCells(map: TiledMap): Set<string> | null {
  const div = collisionDiv(map);
  const W = map.width * div;
  const H = map.height * div;
  const blocked = (x: number, y: number) => x < 0 || y < 0 || x >= W || y >= H || isBlockedSub(map, x, y);
  let sx = Math.floor(W / 2);
  let sy = Math.floor(H / 2);
  let found = false;
  outer: for (let r = 0; r < Math.max(W, H); r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (!blocked(sx + dx, sy + dy)) {
          sx += dx;
          sy += dy;
          found = true;
          break outer;
        }
      }
    }
  }
  if (!found) return null; // 개방 조각이 하나도 없음
  const seenSub = new Set<number>([sy * W + sx]);
  const q: Array<[number, number]> = [[sx, sy]];
  while (q.length) {
    const [x, y] = q.shift()!;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      const nx = x + dx;
      const ny = y + dy;
      const k = ny * W + nx;
      if (!blocked(nx, ny) && !seenSub.has(k)) {
        seenSub.add(k);
        q.push([nx, ny]);
      }
    }
  }
  // 조각 하나라도 닿으면 그 타일은 갈 수 있는 곳
  const seen = new Set<string>();
  for (const k of seenSub) seen.add(`${Math.floor((k % W) / div)},${Math.floor(Math.floor(k / W) / div)}`);
  return seen;
}

/**
 * 맵 이동성 검증: ①포털 도달 가능(트리거 반경 고려 — trigger 속성이 있으면 인접 개방 칸 도달로 충족)
 * ②NPC 인접 도달 가능(대화 가능) ③고립 포켓(개방인데 못 가는 칸) 개수. 문제 목록 반환(빈 배열=통과).
 */
export function validateMapMobility(map: TiledMap): MapIssue[] {
  const issues: MapIssue[] = [];
  const seen = reachableCells(map);
  if (!seen) return [{ kind: "no_walkable", detail: "걸을 수 있는 칸이 하나도 없어요." }];
  const W = map.width;
  const H = map.height;
  const tile = map.tilewidth || 48;
  const cellOf = (o: TiledObject) => ({
    gx: Math.floor((o.x + (o.width ?? tile) / 2) / tile),
    gy: Math.floor((o.y + (o.height ?? tile) / 2) / tile),
  });
  const nearReach = (gx: number, gy: number) =>
    ([[0, 0], [1, 0], [-1, 0], [0, 1], [0, -1]] as const).some(([dx, dy]) => seen.has(`${gx + dx},${gy + dy}`));

  for (const o of listObjects(map)) {
    const { gx, gy } = cellOf(o);
    if (o.type === "portal") {
      const trig = Number(getProp(o, "trigger") ?? 0);
      const ok = trig > tile * 0.5 ? nearReach(gx, gy) : seen.has(`${gx},${gy}`);
      if (!ok) {
        issues.push({
          kind: "portal_unreachable",
          name: String(o.name ?? o.id ?? "?"),
          detail: `포털 "${getProp(o, "title_ko") ?? o.name}"(${gx},${gy})에 걸어서 갈 수 없어요 — 주변 충돌을 열어주세요.`,
        });
      }
    } else if (o.type === "npc") {
      if (!nearReach(gx, gy)) {
        issues.push({
          kind: "npc_unreachable",
          name: String(o.name ?? o.id ?? "?"),
          detail: `NPC "${getProp(o, "title_ko") ?? o.name}"(${gx},${gy}) 곁에 갈 수 없어요.`,
        });
      }
    }
  }

  let pockets = 0;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (!isBlockedTile(map, x, y) && x > 0 && y > 0 && x < W - 1 && y < H - 1 && !seen.has(`${x},${y}`)) pockets++;
    }
  }
  if (pockets > 0) {
    issues.push({
      kind: "isolated_pockets",
      detail: `개방돼 있지만 걸어서 갈 수 없는 칸이 ${pockets}개 있어요(고립 구역).`,
    });
  }
  return issues;
}
